/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Icon from '@material-ui/core/Icon';

import IconButton from '@material-ui/core/IconButton';
import { Link as ScrollLink } from 'react-scroll';
import { useTranslation } from 'react-i18next';
import { CardContext } from '../CardContext';
import EmptyData from './EmptyData';
import CustomEntry from './custom/CustomEntry';
import { scrollerSettings } from '../../../../settings';
import { ConfigResourceProps } from '../../../../redux/enums/ConfigResourceProps';
import { useConfiguration } from '../../../../redux/features/configuration/useConfiguration';

export default function MultiEntry({ sectionHeader, items, sectionIcon, sectionLinkTo, readOnly, hideLinkTo }) {
  const { resource: frontendCustomisation } = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);
  // TODO: harmonize this with DeclarationEntry & PartyEntry -> entry.initialVisibleItems
  // when refactoring the summary cards, take into consideration the naming of 'defaultItemsCount'
  const initiallyVisibleItemsSize = frontendCustomisation?.summary?.card?.initiallyVisibleItemsSize ?? 2;
  const { classes } = useContext(CardContext);
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  return (
    <>
      <Grid item container spacing={2} className={classes.multiEntry}>
        {sectionHeader && (
          <Grid item xs={12}>
            <Typography variant="h6" component="span" style={{justifyContent: 'space-between'}}>
              {' '}
              {sectionHeader}
              {!hideLinkTo && sectionIcon && sectionLinkTo && (
                <>
                  <ScrollLink
                    disabled={readOnly}
                    activeClass="active"
                    to={readOnly ? null : sectionLinkTo}
                    smooth={scrollerSettings.smooth}
                    offset={scrollerSettings.offset}
                    delay={scrollerSettings.delay}
                    duration={scrollerSettings.duration}
                  >
                    <IconButton
                      disabled={readOnly}
                      multi-scroll-link-to={readOnly ? null : sectionLinkTo}
                      aria-label={t('actions.edit')}
                    >
                      <Icon>{sectionIcon}</Icon>
                    </IconButton>
                  </ScrollLink>
                </>
              )}
            </Typography>
          </Grid>
        )}

        {(!items || items?.length === 0) && <EmptyData />}

        {showAll &&
          items &&
          items.map(({ title, value, icon, divider, link, linkTo, type }, index) => {
            return (
              <CustomEntry
                type={type}
                key={title + index}
                title={title}
                value={value}
                icon={icon}
                hasDivider={divider}
                link={link}
                linkTo={linkTo}
                readOnly={readOnly}
              />
            );
          })}

        {!showAll &&
          items &&
          items
            .slice(0, initiallyVisibleItemsSize)
            .map(({ title, value, icon, divider, link, linkTo, type }, index) => {
              return (
                <CustomEntry
                  type={type}
                  key={title + index}
                  title={title}
                  value={value}
                  icon={icon}
                  hasDivider={divider}
                  link={link}
                  linkTo={linkTo}
                  readOnly={readOnly}
                />
              );
            })}

        {showAll && items && items?.length > 0 && (
          <Grid item container spacing={2} className={classes.content}>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                noWrap
                display="inline"
                className={classes.seeAll}
                onClick={() => {
                  setShowAll(false);
                }}
              >
                {t('summary.show.less')}
              </Typography>
            </Grid>
          </Grid>
        )}

        {!showAll && items && items?.length > 0 && items?.length > initiallyVisibleItemsSize && (
          <Grid item container spacing={2} className={classes.content}>
            <Grid item>
              <Typography variant="body2" display="inline" className={classes.seeMoreBox}>
                {'+'.concat(items.length - initiallyVisibleItemsSize)}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                variant="body2"
                noWrap
                display="inline"
                className={classes.seeAll}
                onClick={() => {
                  setShowAll(true);
                }}
              >
                {t('summary.show.all')}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
}
